import https from './https';
import { toast } from './toast';

export interface WishlistItem {
    _id: string;
    name: string;
    price: number | string;
    images?: any[];
    [key: string]: any;
}

let wishlistCache: WishlistItem[] = [];

// Load initial wishlist from localStorage
if (typeof window !== 'undefined') {
    try {
        const stored = localStorage.getItem('honeywill_wishlist');
        if (stored) {
            wishlistCache = JSON.parse(stored).filter((p: any) => p && !(p.name || '').toLowerCase().includes('soft copper pipe pro 1'));
        }
    } catch (e) {
        console.error('Failed to parse cached wishlist', e);
    }
}

const getHeadersToken = () => {
    if (typeof window === 'undefined') return null;
    const token = localStorage.getItem('token');
    return token && token !== 'null' && token !== 'undefined' ? token : null;
};

export const getWishlist = (): WishlistItem[] => {
    return wishlistCache;
};

export const isInWishlist = (productId: string | number | undefined): boolean => {
    if (!productId) return false;
    const idStr = String(productId);
    return wishlistCache.some(item => String(item._id) === idStr);
};

export const fetchWishlist = async (): Promise<WishlistItem[]> => {
    const token = getHeadersToken();
    if (!token) {
        return wishlistCache;
    }

    try {
        const response = await https.get('wishlist/my');
        let products: WishlistItem[] = [];

        if (response && response.success) {
            const data = response.data;
            if (Array.isArray(data)) {
                // If it returns an array of wishlists
                products = data[0]?.products || [];
            } else if (data && typeof data === 'object') {
                // If it returns the wishlist object
                products = data.products || (Array.isArray(data) ? data : []);
            }
        }

        // Filter out dummy product
        wishlistCache = products.filter(p => p && !(p.name || '').toLowerCase().includes('soft copper pipe pro 1'));
        localStorage.setItem('honeywill_wishlist', JSON.stringify(wishlistCache));
        window.dispatchEvent(new Event('wishlistUpdated'));
        return wishlistCache;
    } catch (error) {
        console.error('Error fetching wishlist from server:', error);
        return wishlistCache;
    }
};

export const addToWishlist = async (product: any): Promise<boolean> => {
    const productId = product._id || product.id;
    if (!productId) return false;

    const idStr = String(productId);
    
    // Construct a standard product object for the cache
    const formattedItem: WishlistItem = {
        _id: idStr,
        name: product.name || '',
        price: product.price || 0,
        images: product.images || (product.image ? [{ url: product.image }] : [])
    };

    // Optimistically add to local cache if not already present
    if (!wishlistCache.some(item => String(item._id) === idStr)) {
        wishlistCache = [...wishlistCache, formattedItem];
        localStorage.setItem('honeywill_wishlist', JSON.stringify(wishlistCache));
        window.dispatchEvent(new Event('wishlistUpdated'));
        toast.success("Added to Wishlist successfully!");
    }

    const token = getHeadersToken();
    if (!token) {
        // Not logged in: we still keep it in local storage so they don't lose it
        return true;
    }

    try {
        await https.post('wishlist', { product_id: idStr });
        // Refresh from server to ensure state is completely in-sync
        await fetchWishlist();
        return true;
    } catch (error) {
        console.error('Error adding to server wishlist:', error);
        return false;
    }
};

export const removeFromWishlist = async (productId: string | number): Promise<boolean> => {
    const idStr = String(productId);

    // Optimistically remove from local cache
    const existed = wishlistCache.some(item => String(item._id) === idStr);
    wishlistCache = wishlistCache.filter(item => String(item._id) !== idStr);
    localStorage.setItem('honeywill_wishlist', JSON.stringify(wishlistCache));
    window.dispatchEvent(new Event('wishlistUpdated'));
    if (existed) {
        toast.success("Removed from Wishlist successfully!");
    }

    const token = getHeadersToken();
    if (!token) {
        return true;
    }

    try {
        await https.delete(`wishlist/remove/${idStr}`);
        // Refresh from server to ensure state is completely in-sync
        await fetchWishlist();
        return true;
    } catch (error) {
        console.error('Error removing from server wishlist:', error);
        return false;
    }
};

export const toggleWishlist = async (product: any): Promise<boolean> => {
    const productId = product._id || product.id;
    if (isInWishlist(productId)) {
        return await removeFromWishlist(productId);
    } else {
        return await addToWishlist(product);
    }
};

// Auto fetch when script is imported and user is logged in
if (typeof window !== 'undefined') {
    if (getHeadersToken()) {
        fetchWishlist();
    }
    
    // Re-fetch wishlist if a login event occurs
    window.addEventListener('storage', (e) => {
        if (e.key === 'token') {
            fetchWishlist();
        }
    });
}
